import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standalone Apple Pay button that renders inside the PayPalScriptProvider.
 * The provider must be loaded with `components: "buttons,applepay"`.
 *
 * Visibility rules (per Apple + PayPal spec):
 * - Only renders when window.ApplePaySession is present AND canMakePayments() is true
 * - Only renders when paypal.Applepay().config() returns isEligible: true
 *   (this checks LIVE merchant credentials + verified domain association)
 */
export const ApplePayButton = ({ request, onPaid, onError }) => {
  const [{ isResolved }] = usePayPalScriptReducer();
  const [applepayConfig, setApplepayConfig] = useState(null);
  const [eligible, setEligible] = useState(false);
  const buttonRef = useRef(null);
  const handlerRef = useRef(null);

  useEffect(() => {
    if (!isResolved || !request) return;
    const paypal = window.paypal;
    if (!paypal || !paypal.Applepay) {
      // SDK didn't include applepay component — silently skip
      return;
    }
    // Apple Pay is only available on Safari with the JS API
    if (typeof window.ApplePaySession === "undefined" || !window.ApplePaySession.canMakePayments) {
      return;
    }
    if (!window.ApplePaySession.canMakePayments()) {
      return;
    }
    // Query PayPal for Apple Pay eligibility (domain + merchant approved?)
    (async () => {
      try {
        const cfg = await paypal.Applepay().config();
        if (cfg && cfg.isEligible) {
          setApplepayConfig(cfg);
          setEligible(true);
        }
      } catch (e) {
        // silently swallow — button just won't show
        // eslint-disable-next-line no-console
        console.warn("[ApplePay] config check failed", e);
      }
    })();
  }, [isResolved, request]);

  // Keep latest click handler in a ref so the event listener always calls the latest closure
  useEffect(() => {
    handlerRef.current = () => {
      // CRITICAL: everything up to session.begin() MUST run synchronously in the click
      // handler. iOS Safari silently blocks ApplePaySession if any await happens first.
      if (!request || !applepayConfig) return;
      const paypal = window.paypal;
      const paymentRequest = {
        countryCode: applepayConfig.countryCode || "IT",
        currencyCode: request.currency || "EUR",
        merchantCapabilities: applepayConfig.merchantCapabilities || ["supports3DS"],
        supportedNetworks: applepayConfig.supportedNetworks || ["visa", "masterCard", "amex"],
        requiredBillingContactFields: ["name", "postalAddress"],
        requiredShippingContactFields: ["name", "email"],
        total: {
          label: "Ask Monia Tarot",
          type: "final",
          amount: request.amount.toFixed(2),
        },
      };
      let session;
      try {
        session = new window.ApplePaySession(3, paymentRequest);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ApplePay] ApplePaySession constructor failed", err);
        onError?.(err);
        return;
      }

      // Kick off the PayPal order creation in the background — Apple Pay session
      // does not need the orderId until confirmOrder(), which runs after user auth
      const orderIdPromise = axios
        .post(`${API}/paypal/create-order`, { request_id: request.id })
        .then((r) => r.data.id);

      session.onvalidatemerchant = async (event) => {
        try {
          const validation = await paypal.Applepay().validateMerchant({
            validationUrl: event.validationURL,
            displayName: "Ask Monia Tarot",
          });
          session.completeMerchantValidation(validation.merchantSession);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[ApplePay] validateMerchant error", err);
          session.abort();
          onError?.(err);
        }
      };

      session.onpaymentauthorized = async (event) => {
        try {
          const orderId = await orderIdPromise;
          await paypal.Applepay().confirmOrder({
            orderId,
            token: event.payment.token,
            billingContact: event.payment.billingContact,
            shippingContact: event.payment.shippingContact,
          });
          await axios.post(`${API}/paypal/capture/${orderId}`);
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
          onPaid?.();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[ApplePay] confirmOrder error", err);
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          onError?.(err);
        }
      };

      session.oncancel = () => {};
      // MUST be synchronous relative to the user gesture
      session.begin();
    };
  }, [request, applepayConfig, onPaid, onError]);

  // Attach a native click listener — React onClick does NOT reliably bind to
  // <apple-pay-button> web components (this was the "button visible but not clickable" bug)
  useEffect(() => {
    if (!eligible || !buttonRef.current) return;
    const el = buttonRef.current;
    const onClickNative = (e) => {
      e.preventDefault();
      handlerRef.current && handlerRef.current();
    };
    el.addEventListener("click", onClickNative);
    return () => el.removeEventListener("click", onClickNative);
  }, [eligible]);

  if (!eligible) return null;

  return (
    <div className="w-full" data-testid="apple-pay-container">
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type="pay"
        locale="en"
        style={{
          "--apple-pay-button-width": "100%",
          "--apple-pay-button-height": "44px",
          "--apple-pay-button-border-radius": "999px",
          "--apple-pay-button-padding": "0",
          width: "100%",
          height: "44px",
          cursor: "pointer",
          display: "block",
        }}
        data-testid="apple-pay-button"
      />
    </div>
  );
};

export default ApplePayButton;
